// Payment Tracking convergence — the WF1 allocation drill-in: the heart of
// the tab. Every dollar traceable; evaluated-but-zero and skipped
// priorities render muted (never hidden) so the trail reads complete.
// Servicer-settled lines are labelled — FCI executes those; the run is the
// book of record. The panel is wide (the 7-column priority table reads
// without heavy wrapping) and the full trace can be printed or exported to
// CSV from the footer.

import {
  Button,
  PrintRegion,
  Skeleton,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  printRegion,
} from "@/design-system/components/ui";
import { Download, Printer } from "lucide-react";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { formatCalendarDate } from "@/utils/datetime";
import { useServicingAllocation } from "@/hooks/portfolioAdmin/useServicing";
import type {
  ServicingAllocationDto,
  ServicingAllocationLineDto,
} from "@/api/portfolioAdmin/servicingApi";
import {
  fmtMoney,
  settlementLabel,
  TRACE_STATUS_LABEL,
  traceStatusTone,
} from "./servicingFormat";
import { downloadAllocationCsv } from "./allocationExport";

export function AllocationDrawer({
  paymentId,
  onClose,
  onReverseClick,
  onOpenPayment,
}: {
  paymentId: string | null;
  onClose: () => void;
  /** Fires the "Reverse payment" action for the drawer's payment — the same
   *  contract as ScheduleLedgerTable's row action (the parent owns the
   *  confirmation + required-reason modal and the Payments.Reverse gate;
   *  pass undefined to hide). This is the reverse entry point for payments
   *  whose period sits OUTSIDE the schedule's display horizon (e.g. a
   *  pay-ahead child applied months ahead): the drawer is reachable for ANY
   *  payment via the ?payment= deep-link / Loan Payments ledger, while the
   *  schedule only renders a bounded window. Guard states mirror the row
   *  action: v3 only (never a legacy mirror) and not already reversed;
   *  PIR-instructed / sunset guards live server-side in the shared modal's
   *  preview, identical for both entry points. */
  onReverseClick?: (paymentId: string) => void;
  /** Re-point the drawer to another payment's allocation — used by a pay-ahead
   *  child's "advanced from" banner to open the originating (parent) payment.
   *  When omitted, the banner is text-only. */
  onOpenPayment?: (paymentId: string) => void;
}) {
  const query = useServicingAllocation(paymentId ?? undefined);
  const dto = query.data;

  const canReverseThis =
    !!onReverseClick && !!dto && !dto.isLegacyMirror && dto.status === "processed";

  const footer = dto ? (
    <>
      {canReverseThis && (
        <Button
          variant="secondary"
          size="sm"
          className="text-status-error"
          onClick={() => onReverseClick!(dto.paymentId)}
          data-testid="allocation-reverse"
        >
          Reverse
        </Button>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => downloadAllocationCsv(dto)}
        data-testid="allocation-export-csv"
      >
        <Download size={14} aria-hidden />
        Export CSV
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => printRegion()}
        data-testid="allocation-print"
      >
        <Printer size={14} aria-hidden />
        Print
      </Button>
    </>
  ) : undefined;

  return (
    <ResourceDrawer
      open={!!paymentId}
      onClose={onClose}
      title="Waterfall allocation"
      subtitle={dto ? `${formatCalendarDate(dto.paymentDate)} · ${fmtMoney(dto.amount)}` : undefined}
      width="xl"
      footer={footer}
      testId="allocation-drawer"
    >
      {query.isLoading && (
        <div className="flex flex-col gap-space-2" data-testid="allocation-loading">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
        </div>
      )}
      {query.isError && (
        <div className="flex flex-col items-start gap-space-2" data-testid="allocation-error">
          <p className="text-small text-status-error m-0">
            The allocation could not be loaded.
          </p>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {dto && (
        <div className="flex flex-col gap-space-4">
          {dto.isLegacyMirror && (
            <p className="text-small text-gray-mid m-0" data-testid="allocation-legacy-note">
              Mirrored from a pre-v3 waterfall run — read-only history.
            </p>
          )}
          {dto.paidAheadFromPaymentId && (
            <div
              className="flex items-center justify-between gap-space-2 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
              data-testid="allocation-pay-ahead-note"
            >
              <p className="text-small text-gray-mid m-0">
                Pay-ahead installment — advanced from an earlier recorded payment.
              </p>
              {onOpenPayment && (
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => onOpenPayment(dto.paidAheadFromPaymentId!)}
                  data-testid="allocation-view-parent"
                >
                  View originating payment
                </Button>
              )}
            </div>
          )}
          <dl className="grid grid-cols-2 gap-x-space-4 gap-y-space-1 m-0 text-small">
            <Fact label="Payment type" value={dto.paymentType.replaceAll("_", " ")} />
            <Fact label="Status" value={dto.status} />
            <Fact label="Interest portion" value={fmtMoney(dto.interestPortion)} />
            <Fact label="Principal portion" value={fmtMoney(dto.principalPortion)} />
            <Fact label="Balance after" value={fmtMoney(dto.principalBalanceAfter)} />
            <Fact label="Distributed / residual"
              value={`${fmtMoney(dto.totalDistributed)} / ${fmtMoney(dto.residual)}`} />
          </dl>

          <Table data-testid="allocation-lines">
            <THead>
              <TR>
                <TH className="whitespace-nowrap">P</TH>
                <TH>Obligation</TH>
                <TH>Recipient</TH>
                <TH className="text-right whitespace-nowrap">Amount</TH>
                <TH className="text-right whitespace-nowrap">Obligation after</TH>
                <TH className="whitespace-nowrap">Settles via</TH>
                <TH className="whitespace-nowrap">Status</TH>
              </TR>
            </THead>
            <TBody>
              {dto.lines.map((line, i) => (
                <AllocationLine key={`${line.position}-${line.disbursementId ?? i}`} line={line} />
              ))}
            </TBody>
          </Table>

          {/* Clean, body-level rendering printed when the footer Print button
              fires — the whole trace + summary, free of the app chrome. */}
          <PrintRegion>
            <AllocationPrintView dto={dto} formatDate={formatCalendarDate} />
          </PrintRegion>
        </div>
      )}
    </ResourceDrawer>
  );
}

function AllocationLine({ line }: { line: ServicingAllocationLineDto }) {
  const muted = line.status !== "fired";
  return (
    <TR
      className={muted ? "text-gray-mid" : undefined}
      data-testid={`allocation-line-${line.position}`}
    >
      <TD className="tabular-nums">{line.position}</TD>
      <TD>
        {line.obligationTypeDisplay ?? line.obligationTypeCode}
        {line.reason && (
          <span className="block text-caption italic text-gray-mid">{line.reason}</span>
        )}
      </TD>
      <TD>{line.recipientName ?? "—"}</TD>
      <TD className="text-right tabular-nums whitespace-nowrap">{fmtMoney(line.amount)}</TD>
      <TD className="text-right tabular-nums whitespace-nowrap">
        {line.obligationBalanceAfter !== null ? fmtMoney(line.obligationBalanceAfter) : "—"}
      </TD>
      <TD className="whitespace-nowrap">{settlementLabel(line.settlementChannel) || "—"}</TD>
      <TD className="whitespace-nowrap">
        <StatusBadge tone={traceStatusTone(line.status)}>
          {TRACE_STATUS_LABEL[line.status]}
        </StatusBadge>
      </TD>
    </TR>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-gray-mid m-0">{label}</dt>
      <dd className="m-0 text-right tabular-nums">{value}</dd>
    </>
  );
}

// Print-only rendering: plain semantic tables on white (the design-system
// Table's dark gold-on-onyx header wastes ink and prints poorly), with the
// header summary above the full priority trace. Reuses the same formatters
// as the screen view so the figures match exactly.
function AllocationPrintView({
  dto,
  formatDate,
}: {
  dto: ServicingAllocationDto;
  formatDate: (iso: string) => string;
}) {
  return (
    <div data-testid="allocation-print-view" className="p-space-5 text-onyx">
      <p className="font-display font-bold text-h3 tracking-normal m-0">
        Waterfall allocation
      </p>
      <p className="text-caption text-gray-mid mt-space-1 mb-space-4">
        {formatDate(dto.paymentDate)} · {fmtMoney(dto.amount)}
      </p>

      <table className="text-small border-collapse mb-space-5">
        <tbody>
          <PrintFact label="Payment type" value={dto.paymentType.replaceAll("_", " ")} />
          <PrintFact label="Status" value={dto.status} />
          <PrintFact label="Interest portion" value={fmtMoney(dto.interestPortion)} />
          <PrintFact label="Principal portion" value={fmtMoney(dto.principalPortion)} />
          <PrintFact label="Balance after" value={fmtMoney(dto.principalBalanceAfter)} />
          <PrintFact
            label="Distributed / residual"
            value={`${fmtMoney(dto.totalDistributed)} / ${fmtMoney(dto.residual)}`}
          />
        </tbody>
      </table>

      <table className="w-full text-small border-collapse [&_th]:border [&_td]:border [&_th]:border-border-light [&_td]:border-border-light [&_th]:px-space-2 [&_th]:py-space-1 [&_td]:px-space-2 [&_td]:py-space-1 [&_th]:text-left [&_th]:align-bottom">
        <thead>
          <tr>
            <th>P</th>
            <th>Obligation</th>
            <th>Recipient</th>
            <th className="text-right">Amount</th>
            <th className="text-right">Obligation after</th>
            <th>Settles via</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {dto.lines.map((line, i) => (
            <tr key={`${line.position}-${line.disbursementId ?? i}`}>
              <td className="tabular-nums">{line.position}</td>
              <td>
                {line.obligationTypeDisplay ?? line.obligationTypeCode}
                {line.reason ? ` — ${line.reason}` : ""}
              </td>
              <td>{line.recipientName ?? "—"}</td>
              <td className="text-right tabular-nums">{fmtMoney(line.amount)}</td>
              <td className="text-right tabular-nums">
                {line.obligationBalanceAfter !== null
                  ? fmtMoney(line.obligationBalanceAfter)
                  : "—"}
              </td>
              <td>{settlementLabel(line.settlementChannel) || "—"}</td>
              <td>{TRACE_STATUS_LABEL[line.status]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PrintFact({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td className="pr-space-4 text-gray-mid align-top">{label}</td>
      <td className="font-medium tabular-nums">{value}</td>
    </tr>
  );
}
